import type { Editor, JSONContent, Range } from '@tiptap/core';
import type { Node as ProseMirrorNode } from '@tiptap/pm/model';

import { Node, mergeAttributes } from '@tiptap/core';
import { Fragment } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import { mergeFieldRegex } from 'utility/merge-fields';
import { MergeFieldPill } from './components/MergeFieldPill';
import { getMergeFieldSuggestionOptions } from './suggestions/merge-field';

const MergeFieldPluginKey = new PluginKey('mergeField');

// tiptap does not sanitize `<` and `>` in attribute values
// like it does for `"`, `&` etc,
// so we need to sanitize them to prevent content to be broken,
// what we actually care about is sanitizing `<`
const escapeDataAttribute = (value: string | null) => {
  return (value || '').replace(/</g, '&lt;');
};
const unescapeDataAttribute = (value: string | null) => {
  return (value || '').replace(/&lt;/g, '<');
};

export const parseMergeFieldLine = (content: string): JSONContent => {
  let match;
  let cursor = 0;
  const inlineContent = [];
  const regex = mergeFieldRegex.text();

  while ((match = regex.exec(content)) !== null) {
    if (match.index > cursor) {
      const text = content.slice(cursor, match.index);

      if (text) {
        inlineContent.push({ type: 'text', text });
      }
    }

    inlineContent.push({
      type: 'mergeField',
      attrs: {
        relationship: match[2],
        unescaped: match[1] === '{{{',
      },
    });

    cursor = match.index + match[0].length;
  }

  if (cursor < content.length) {
    const text = content.slice(cursor);

    if (text) {
      inlineContent.push({ type: 'text', text });
    }
  }

  if (inlineContent.length === 0) {
    return { type: 'paragraph', content: [] };
  }

  return { type: 'paragraph', content: inlineContent };
};

export const MergeField = Node.create({
  name: 'mergeField',

  addOptions() {
    return {
      HTMLAttributes: {
        class: 'kzn-merge-field',
      },
      unavailableMergeFields: [],
      renderLabel(node: ProseMirrorNode) {
        if (node.attrs.unescaped) {
          return `{{{ ${node.attrs.relationship} }}}`;
        }

        return `{{ ${node.attrs.relationship} }}`;
      },
      suggestion: {
        char: '{{',
        allowSpaces: true,
        pluginKey: MergeFieldPluginKey,
        allow: ({ editor, range }: { editor: Editor; range: Range }) => {
          // copied from: https://github.com/ueberdosis/tiptap/blob/9afadeb7fe368f95064f84424d6a3dd6cd85b43d/packages/extension-mention/src/mention.ts#L54-L60
          const $from = editor.state.doc.resolve(range.from);
          const type = editor.schema.nodes[this.name];
          const allow = !!$from.parent.type.contentMatch.matchType(type);
          return allow;
        },
        ...getMergeFieldSuggestionOptions(),
      },
    };
  },

  group: 'inline',

  inline: true,

  selectable: true,

  atom: true,

  addAttributes() {
    return {
      label: {
        default: null,
        parseHTML: (element) => {
          return unescapeDataAttribute(
            element.getAttribute('data-merge-field-fallback-label')
          );
        },
        renderHTML: (attributes) => {
          return attributes.label
            ? {
                'data-merge-field-fallback-label': escapeDataAttribute(
                  attributes.label
                ),
              }
            : {};
        },
      },
      relationship: {
        default: null,
        parseHTML: (element) => {
          return unescapeDataAttribute(
            element.getAttribute('data-merge-field-relationship')
          );
        },
        renderHTML: (attributes) => {
          return attributes.relationship
            ? {
                'data-merge-field-relationship': escapeDataAttribute(
                  attributes.relationship
                ),
              }
            : {};
        },
      },
      objectname: {
        default: null,
        parseHTML: (element) => {
          return unescapeDataAttribute(
            element.getAttribute('data-merge-field-objectname')
          );
        },
        renderHTML: (attributes) => {
          return attributes.objectname
            ? {
                'data-merge-field-objectname': escapeDataAttribute(
                  attributes.objectname
                ),
              }
            : {};
        },
      },
      unescaped: {
        default: false,
        parseHTML: (element) => {
          return element.hasAttribute('data-merge-field-unescaped');
        },
        renderHTML: (attributes) => {
          return attributes.unescaped
            ? { 'data-merge-field-unescaped': true }
            : {};
        },
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span.kzn-merge-field',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes),
      this.options.renderLabel(node),
    ];
  },

  renderText({ node }) {
    return this.options.renderLabel(node);
  },

  addNodeView() {
    return ReactNodeViewRenderer(MergeFieldPill);
  },

  addProseMirrorPlugins() {
    const type = this.type;

    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
      new Plugin({
        key: new PluginKey('mergeFieldPaste'),
        props: {
          handlePaste: (view, event) => {
            const text = event.clipboardData?.getData('text/plain');

            if (!text) {
              return false;
            }

            const matches = [];
            const regex = mergeFieldRegex.text();
            let match;

            while ((match = regex.exec(text)) !== null) {
              matches.push({
                index: match.index,
                length: match[0].length,
                relationship: match[2],
                unescaped: match[1] === '{{{',
              });
            }

            if (matches.length === 0) {
              return false;
            }

            const { dispatch, state } = view;
            const { tr, selection } = state;
            const fragments = [];
            let cursor = 0;

            for (const match of matches) {
              if (match.index > cursor) {
                fragments.push(
                  state.schema.text(text.slice(cursor, match.index))
                );
              }

              fragments.push(
                type.create({
                  relationship: match.relationship,
                  unescaped: match.unescaped,
                })
              );

              cursor = match.index + match.length;
            }

            if (cursor < text.length) {
              fragments.push(state.schema.text(text.slice(cursor)));
            }

            const fragment = Fragment.from(fragments);
            tr.replaceWith(selection.from, selection.to, fragment);
            dispatch(tr);
            return true;
          },
        },
      }),
    ];
  },
});
